import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Calendar } from '@codes/ui-kit/components/ui/calendar';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@codes/ui-kit/components/ui/popover';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { LogView } from '@lemoncloud/codes-backend-api';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';

import { useGenerateLogs, useSaveLogsMeta } from '@codes/logs';
import type { GenerateLogsBody } from '@codes/logs';

interface LogGeneratorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const LogGeneratorModal = ({ open, onOpenChange }: LogGeneratorModalProps) => {
    const { t } = useTranslation();

    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
    const [toDate, setToDate] = useState<Date | undefined>(undefined);
    const [previewLogs, setPreviewLogs] = useState<LogView[]>([]);
    const [previewBody, setPreviewBody] = useState<GenerateLogsBody | null>(null);

    const generateLogsMutation = useGenerateLogs();
    const saveLogsMetaMutation = useSaveLogsMeta();

    const resetForm = () => {
        setUserId('');
        setUserName('');
        setFromDate(undefined);
        setToDate(undefined);
        setPreviewLogs([]);
        setPreviewBody(null);
    };

    const handleClose = () => {
        resetForm();
        onOpenChange(false);
    };

    const handlePreview = async () => {
        if (!userId || !userName || !fromDate || !toDate) {
            toast({ title: t('logs.generator.fillAllFields'), variant: 'destructive' });
            return;
        }

        const body: GenerateLogsBody = {
            userId,
            userName,
            from: format(fromDate, 'yyyy-MM-dd'),
            to: format(toDate, 'yyyy-MM-dd'),
        };

        try {
            const result = await generateLogsMutation.mutateAsync({ body, save: false });
            setPreviewLogs(result.list || []);
            setPreviewBody(body);
            toast({ title: t('logs.generator.previewSuccess', { count: result.list?.length || 0 }) });
        } catch (error) {
            console.error('Failed to generate preview:', error);
            toast({ title: t('logs.generator.previewFailed'), variant: 'destructive' });
        }
    };

    const handleSave = async () => {
        if (!previewBody || previewLogs.length === 0) {
            toast({ title: t('logs.generator.previewFirst'), variant: 'destructive' });
            return;
        }

        try {
            await saveLogsMetaMutation.mutateAsync({
                id: `LG:${previewBody.userId}`,
                name: previewBody.userName,
                stereo: 'log',
                meta: JSON.stringify(previewLogs),
            });
            toast({ title: t('logs.generator.saveSuccess') });
            handleClose();
        } catch (error) {
            console.error('Failed to save logs:', error);
            toast({ title: t('logs.generator.saveFailed'), variant: 'destructive' });
        }
    };

    const isLoading = generateLogsMutation.isPending || saveLogsMetaMutation.isPending;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]" showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle>{t('logs.generator.title')}</DialogTitle>
                </DialogHeader>

                <p className="text-sm text-muted-foreground text-center px-6 pt-4 pb-2">
                    {t('logs.generator.description')}
                </p>

                <div className="space-y-4 py-4 px-6">
                    {/* User ID */}
                    <div className="space-y-2">
                        <Label htmlFor="userId">{t('logs.generator.userId')}</Label>
                        <Input
                            id="userId"
                            value={userId}
                            onChange={e => setUserId(e.target.value)}
                            placeholder="1000082"
                            disabled={isLoading}
                        />
                    </div>

                    {/* User Name */}
                    <div className="space-y-2">
                        <Label htmlFor="userName">{t('logs.generator.userName')}</Label>
                        <Input
                            id="userName"
                            value={userName}
                            onChange={e => setUserName(e.target.value)}
                            placeholder={t('logs.generator.companyNamePlaceholder')}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Date Range */}
                    <div className="space-y-2">
                        <Label>{t('logs.generator.dateRange')}</Label>
                        <div className="flex items-center gap-2">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="flex-1 justify-start text-left font-normal"
                                        disabled={isLoading}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {fromDate ? format(fromDate, 'yyyy-MM-dd') : t('logs.generator.startDate')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar mode="single" selected={fromDate} onSelect={setFromDate} initialFocus />
                                </PopoverContent>
                            </Popover>
                            <span className="text-muted-foreground">~</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="flex-1 justify-start text-left font-normal"
                                        disabled={isLoading}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {toDate ? format(toDate, 'yyyy-MM-dd') : t('logs.generator.endDate')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={toDate}
                                        onSelect={setToDate}
                                        disabled={date => (fromDate ? date < fromDate : false)}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    {/* Preview Button */}
                    <div className="flex justify-end pt-2">
                        <Button onClick={handlePreview} disabled={isLoading} variant="secondary">
                            {generateLogsMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('logs.generator.preview')}
                        </Button>
                    </div>

                    {/* Preview Table */}
                    {previewLogs.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="bg-muted px-4 py-2 font-medium text-sm">
                                {t('logs.generator.previewResult', { count: previewLogs.length })}
                            </div>
                            <div className="max-h-[200px] overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium">
                                                {t('logs.generator.table.date')}
                                            </th>
                                            <th className="px-3 py-2 text-left font-medium">
                                                {t('logs.generator.table.loginAt')}
                                            </th>
                                            <th className="px-3 py-2 text-right font-medium">
                                                {t('logs.generator.table.apiCalls')}
                                            </th>
                                            <th className="px-3 py-2 text-right font-medium">
                                                {t('logs.generator.table.duration')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewLogs.slice(0, 50).map((log, index) => (
                                            <tr key={index} className="border-t hover:bg-muted/30">
                                                <td className="px-3 py-2">{log.loginAt?.split(' ')[0]}</td>
                                                <td className="px-3 py-2">{log.loginAt?.split(' ')[1]}</td>
                                                <td className="px-3 py-2 text-right">{log.apiCallCount}</td>
                                                <td className="px-3 py-2 text-right">{log.duration}s</td>
                                            </tr>
                                        ))}
                                        {previewLogs.length > 50 && (
                                            <tr className="border-t bg-muted/30">
                                                <td colSpan={4} className="px-3 py-2 text-center text-muted-foreground">
                                                    {t('logs.generator.table.moreRows', {
                                                        count: previewLogs.length - 50,
                                                    })}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex justify-end gap-2 px-6 pb-6">
                    <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                        {t('logs.generator.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={isLoading || previewLogs.length === 0}>
                        {saveLogsMetaMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('logs.generator.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
